export type Rotation = 0 | 90 | 180 | 270;

/**
 * Format a normalized [0,1] coordinate as a CSS percentage string without
 * binary float noise (`0.55 * 100` → `"55.00000000000001%"`).
 */
export function toCssPercent(normalized: number, digits = 6): string {
  return `${+(normalized * 100).toFixed(digits)}%`;
}

/**
 * Transform a point from image-natural normalized space (u, v) to the displayed
 * (post-rotation) normalized space (rx, ry). Both spaces are [0,1]². Aspect
 * ratio does not factor in: the transform is a pure rotation of the unit square
 * by `rotation` degrees clockwise about its center.
 *
 * Screen convention (y grows downward): 90° CW maps top-left (0,0) to top-right
 * (1,0), top-right (1,0) to bottom-right (1,1), etc.
 */
export function rotatePoint(
  rel: { x: number; y: number },
  rotation: Rotation,
): { x: number; y: number } {
  const { x: u, y: v } = rel;
  switch (rotation) {
    case 0:
      return { x: u, y: v };
    case 90:
      return { x: 1 - v, y: u };
    case 180:
      return { x: 1 - u, y: 1 - v };
    case 270:
      return { x: v, y: 1 - u };
  }
}

/**
 * Inverse of `rotatePoint`: transform a point from displayed (post-rotation)
 * normalized space back to image-natural normalized space. Used for preserving
 * drag-end positions in natural coords before saving to the backend.
 */
export function inverseRotatePoint(
  rel: { x: number; y: number },
  rotation: Rotation,
): { x: number; y: number } {
  const { x: rx, y: ry } = rel;
  switch (rotation) {
    case 0:
      return { x: rx, y: ry };
    case 90:
      return { x: ry, y: 1 - rx };
    case 180:
      return { x: 1 - rx, y: 1 - ry };
    case 270:
      return { x: 1 - ry, y: rx };
  }
}

/**
 * Transform a drag delta from the displayed (post-rotation) frame back into
 * image-natural space. The displayed delta is measured in normalized rotated-
 * frame units (e.g. dx_px / rect.width where rect is the rotated wrapper's
 * bounding box). Adding the returned delta to a natural-space rel_x/rel_y
 * yields the new natural-space position.
 */
export function rotateDelta(
  delta: { dx: number; dy: number },
  rotation: Rotation,
): { dx: number; dy: number } {
  const { dx, dy } = delta;
  switch (rotation) {
    case 0:
      return { dx, dy };
    case 90:
      return { dx: dy, dy: -dx };
    case 180:
      return { dx: -dx, dy: -dy };
    case 270:
      return { dx: -dy, dy: dx };
  }
}

/**
 * Cycle 0 → 90 → 180 → 270 → 0. Explicit switch avoids casting a `number`
 * result back to the `Rotation` union literal.
 */
export function nextRotation(current: Rotation): Rotation {
  switch (current) {
    case 0:
      return 90;
    case 90:
      return 180;
    case 180:
      return 270;
    case 270:
      return 0;
  }
}
